<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType, status } =
  useMessageContext();
const { t } = useI18n();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const contactInfoRequestState = computed(() => {
  if (status.value === MESSAGE_STATUS.FAILED) return null;

  return contentAttributes.value?.whatsappContactInfo?.state;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const whatsappInteractiveBadge = computed(() => {
  const attributes = contentAttributes.value || {};
  const reactionInfo = attributes.whatsappReaction || attributes.whatsapp_reaction;
  if (reactionInfo?.emoji) {
    return {
      label: t('CONVERSATION.INTERACTIVE_BADGES.REACTION'),
      value: reactionInfo.emoji,
      target: reactionInfo.targetMessagePreview || reactionInfo.target_message_preview,
    };
  }

  const flowInfo = attributes.whatsappFlow || attributes.whatsapp_flow;
  if (flowInfo) {
    return {
      label: t('CONVERSATION.INTERACTIVE_BADGES.FLOW'),
      value: flowInfo.name,
    };
  }

  const commerceInfo =
    attributes.whatsappCommerce || attributes.whatsapp_commerce;
  if (commerceInfo?.type === 'product_inquiry') {
    return {
      label: t('CONVERSATION.INTERACTIVE_BADGES.PRODUCT_ENQUIRY'),
      value:
        commerceInfo.productRetailerId || commerceInfo.product_retailer_id,
    };
  }

  if (commerceInfo?.type === 'product_order') {
    return {
      label: t('CONVERSATION.INTERACTIVE_BADGES.PRODUCT_ORDER'),
      value:
        commerceInfo.summary ||
        commerceInfo.productRetailerIds?.join(', ') ||
        commerceInfo.product_retailer_ids?.join(', '),
    };
  }

  if (commerceInfo?.type === 'catalog') {
    return {
      label: t('CONVERSATION.INTERACTIVE_BADGES.CATALOG'),
      value: commerceInfo.catalogName || commerceInfo.productName,
    };
  }

  if (commerceInfo?.type === 'product') {
    return {
      label: t('CONVERSATION.INTERACTIVE_BADGES.PRODUCT'),
      value: commerceInfo.productName || commerceInfo.catalogName,
    };
  }

  if (commerceInfo?.type === 'multi_product') {
    return {
      label: t('CONVERSATION.INTERACTIVE_BADGES.MULTI_PRODUCT'),
      value: commerceInfo.productNames?.length
        ? commerceInfo.productNames.join(', ')
        : commerceInfo.catalogName,
    };
  }

  return null;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <div
        v-if="whatsappInteractiveBadge"
        class="flex flex-wrap items-center gap-2"
      >
        <span
          class="inline-flex rounded-full bg-n-alpha-2 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.02em] text-n-brand"
        >
          {{ whatsappInteractiveBadge.label }}
        </span>
        <span
          v-if="whatsappInteractiveBadge.value"
          class="text-xs text-n-slate-11"
        >
          {{ whatsappInteractiveBadge.value }}
        </span>
        <span
          v-if="whatsappInteractiveBadge.target"
          class="text-xs text-n-slate-10"
        >
          {{
            $t('CONVERSATION.INTERACTIVE_BADGES.REACTION_TARGET', {
              target: whatsappInteractiveBadge.target,
            })
          }}
        </span>
      </div>
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <span
        v-if="contactInfoRequestState === 'pending'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.PENDING') }}
      </span>
      <span
        v-else-if="contactInfoRequestState === 'shared'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.SHARED') }}
      </span>
      <span
        v-else-if="contactInfoRequestState === 'identity_conflict'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.IDENTITY_CONFLICT') }}
      </span>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
